import { createGlobalState, useStorage } from '@vueuse/core';
import type { Dayjs } from 'dayjs';
import { computed, nextTick, ref } from 'vue';
import dayjs from '~/utils/dayjs';
import { randomString } from '~/utils/string';

type CalloutType = 'info' | 'success' | 'danger' | 'primary' | 'violet' | 'slate';

type CalloutAction = {
    label?: string;
    text?: string;
    href?: string;
    onClick?: (event: MouseEvent) => void;
} & ({ label: string } | { text: string });

interface Callout {
    id: string;
    section: string;
    message: string;
    type: CalloutType;
    ttl?: Dayjs;
    action?: CalloutAction;
    closeable: boolean;
    shadow: boolean;
    calm: boolean;
}

interface CalloutOptions {
    type?: CalloutType;
    ttl?: Dayjs | number;
    action?: CalloutAction;
    closeable?: boolean;
    shadow?: boolean;
    calm?: boolean;
}

export const useCallout = createGlobalState(() => {
    const callouts = ref<Callout[]>([]);
    const persistedCallouts = useStorage<Callout[]>('voxie-page-callouts', [], sessionStorage);

    const generateId = (section: string): string => {
        return `callout_${section}_${randomString()}`;
    };

    const cleanExpired = () => {
        const now = dayjs();
        persistedCallouts.value = persistedCallouts.value.filter((callout) => {
            if (!callout.ttl) {
                return true;
            }
            return dayjs(callout.ttl).isAfter(now);
        });
    };

    const show = (section: string, message: string, options: CalloutOptions = {}): string => {
        cleanExpired();

        const { type = 'info', ttl, action, closeable = false, shadow = false, calm = false } = options;

        const resolvedTtl = typeof ttl === 'number' ? dayjs().add(ttl, 'seconds') : ttl;

        const callout: Callout = {
            id: generateId(section),
            section,
            message,
            type,
            ttl: resolvedTtl,
            action,
            closeable,
            shadow,
            calm,
        };

        if (resolvedTtl) {
            persistedCallouts.value.unshift(callout);
        } else {
            callouts.value.unshift(callout);
        }

        nextTick().then(() => {
            const rect = document.querySelector('.page-callout-container')?.getBoundingClientRect?.();
            window.scrollTo({
                top: window.scrollY + (rect?.top || 0) - 80,
                behavior: 'smooth',
            });
        });

        return callout.id;
    };

    const success = (section: string, message: string, options: CalloutOptions = {}): string => {
        return show(section, message, { ...options, type: 'success' });
    };

    const danger = (section: string, message: string, options: CalloutOptions = {}): string => {
        return show(section, message, { ...options, type: 'danger' });
    };

    const error = (section: string, message: string, options: CalloutOptions = {}): string => {
        return show(section, message, { ...options, type: 'danger' });
    };

    const info = (section: string, message: string, options: CalloutOptions = {}): string => {
        return show(section, message, { ...options, type: 'info' });
    };

    const primary = (section: string, message: string, options: CalloutOptions = {}): string => {
        return show(section, message, { ...options, type: 'primary' });
    };

    const violet = (section: string, message: string, options: CalloutOptions = {}): string => {
        return show(section, message, { ...options, type: 'violet' });
    };

    const slate = (section: string, message: string, options: CalloutOptions = {}): string => {
        return show(section, message, { ...options, type: 'slate' });
    };

    const remove = (calloutId: string): void => {
        callouts.value = callouts.value.filter((callout) => callout.id !== calloutId);
        persistedCallouts.value = persistedCallouts.value.filter((callout) => callout.id !== calloutId);
    };

    const clear = (section?: string): void => {
        if (section) {
            callouts.value = callouts.value.filter((callout) => callout.section !== section);
            persistedCallouts.value = persistedCallouts.value.filter((callout) => callout.section !== section);
        } else {
            callouts.value = [];
            persistedCallouts.value = [];
        }
    };

    const forSection = (section: string): Callout[] => {
        const currentCallouts = callouts.value.filter((callout) => callout.section === section);
        const ttlCallouts = persistedCallouts.value.filter((callout) => callout.section === section);

        return [...currentCallouts, ...ttlCallouts];
    };

    const allCallouts = computed(() => {
        return [...callouts.value, ...persistedCallouts.value];
    });

    cleanExpired();

    return {
        callouts: allCallouts,
        show,
        success,
        danger,
        error,
        info,
        primary,
        violet,
        slate,
        clear,
        remove,
        forSection,
    };
});
